/* eslint-disable no-console */
/* eslint-disable react/prop-types */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { memo, useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import DashboardCard from "components/DashboardCard";

import Footer from "components/Footer";

import { getOnnaDevicesState } from "store/actions/onnaDevicesActions";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { getServiceEvents, getAllServices } from "store/actions/servicesActions";
import { getAllAmenities, getAmenityEvents } from "store/actions/amenitiesActions";
import supportCaseUtils from "layouts/supportCases/supportCaseUtils";

import { getAllSupportCases } from "store/actions/supportActions";

import { updateClients } from "store/actions/clientsActions";
import { updateClientReservations } from "store/actions/clientReservationsActions";
import { getAllChatMessages } from "store/actions/chatActions";

const { getActiveClientReservationIdsForSupportCases, isSupportActive } = supportCaseUtils;

function Dashboard({
  sendGetOnnaDevicesState,
  onnaDevices,
  onnaDevicesData,
  apartmentsInError,
  menuConfig,
  projects,
  supportCases,
  supportSummary,
  supportEvents,
  chatSummary,
  chatEvents,
  amenityEvents,
  amenityEventsSummary,
  serviceEvents,
  serviceEventsSummary,
  actGetAllServiceEvents,
  actGetServices,
  actGetAmenities,
  actGetAllAmenityEvents,
  actGetAllSupportCases,
  actGetAllChatMessages,
  actGetClients,
  actGetClientReservations,
  mainUserId,
  isProjectManager,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // console.log({ currentProyectId, mainUserId });
  // console.log({ currentProyectId, amenityEvents, serviceEvents });
  // console.log({ supportCases });
  // Ranges per widget
  const [supportRange, setSupportRange] = useState("today");
  const [servicesRange, setServicesRange] = useState("today");
  const [areasRange, setAreasRange] = useState("today");

  let chatEnabled = false;
  let amenitiesEnabled = false;
  let servicesEnabled = false;
  let supportEnabled = false;
  let notificationsEnabled = true;
  let onnaDevicesEnabled = true;

  const projectsArray = Object.values(projects || {});

  projectsArray.forEach((project) => {
    const { features = {} } = project;
    if (Object.hasOwn(features, "chat")) {
      chatEnabled = features.chat;
    }
    if (Object.hasOwn(features, "amenities")) {
      amenitiesEnabled = features.amenities;
    }
    if (Object.hasOwn(features, "services")) {
      servicesEnabled = features.services;
    }
    if (Object.hasOwn(features, "support")) {
      supportEnabled = features.support;
    }
    if (isProjectManager) {
      notificationsEnabled = features.notifications;
      onnaDevicesEnabled = features.devices;
    }
  });

  // Example of >10 items (to show "more...")
  useEffect(() => {
    sendGetOnnaDevicesState(Object.keys(onnaDevices));
  }, [JSON.stringify(onnaDevices), JSON.stringify(supportCases)]);

  useEffect(() => {
    actGetClients();
    actGetClientReservations();
    actGetServices();
    actGetAllServiceEvents();
    actGetAmenities();
    actGetAllAmenityEvents();
    actGetAllSupportCases();
    actGetAllChatMessages();
  }, [mainUserId]);

  const onnaDevicesLabel = t(
    menuConfig?.onnaDevices?.label ?? menuConfig?.onnaDevices?.defaultLabel ?? "onnaDevices"
  );

  return (
    <DashboardLayout>
      <DashboardNavbar title="dashboard" route="/" />
      <MDBox>
        <MDBox pt={6}>
          {/* Grid container remains the same, but the children change */}
          <Grid container spacing={3} mb={6}>
            {/* Support Card */}
            {supportEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={t("support").toUpperCase()}
                  icon="support_agent_icon"
                  summaryItems={[
                    { label: t("supportOpen"), value: supportSummary.open },
                    { label: t("supportInProgress"), value: supportSummary.inProgress },
                    { label: t("supportCompleted"), value: supportSummary.solved },
                  ]}
                  range={supportRange}
                  onRangeChange={setSupportRange}
                  showFilter={false}
                  showEvents
                  events={supportEvents}
                  onClick={() => navigate("/support")}
                />
              </Grid>
            )}

            {/* Paid Services Card */}
            {servicesEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={t("serviceEvents")}
                  icon="room_service"
                  summaryItems={[
                    { label: t("serviceEventsToday"), value: serviceEventsSummary.today },
                    { label: t("serviceEventsUpcoming"), value: serviceEventsSummary.upcoming },
                  ]}
                  range={servicesRange}
                  showFilter={false}
                  showEvents
                  onRangeChange={setServicesRange}
                  events={serviceEvents}
                  onClick={() => navigate("/services")}
                />
              </Grid>
            )}

            {/* Amenities Card */}
            {amenitiesEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={t("amenityEvents")}
                  icon="book"
                  summaryItems={[
                    { label: t("reservationsToday"), value: amenityEventsSummary.today },
                    { label: t("reservationsNext"), value: amenityEventsSummary.upcoming },
                  ]}
                  range={areasRange}
                  onRangeChange={setAreasRange}
                  showFilter={false}
                  showEvents
                  events={amenityEvents}
                  onClick={() => navigate("/amenities")}
                />
              </Grid>
            )}

            {/* Chat Card */}
            {chatEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={t("chat")}
                  icon="chat"
                  summaryItems={[
                    { label: t("notRead"), value: chatSummary.unread },
                    { label: t("active"), value: chatSummary.active },
                    // { label: t("total"), value: chatSummary.total },
                  ]}
                  range={areasRange}
                  onRangeChange={setAreasRange}
                  showFilter={false}
                  showEvents={false}
                  events={chatEvents}
                  onClick={() => navigate("/chat")}
                />
              </Grid>
            )}

            {/* Devices Card */}
            {onnaDevicesEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={onnaDevicesLabel}
                  icon="hub"
                  summaryItems={onnaDevicesData.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))}
                  range={areasRange}
                  onRangeChange={setAreasRange}
                  showFilter={false}
                  showEvents={false}
                  events={[]}
                  onClick={() => console.log("Go to Devices")}
                />
              </Grid>
            )}
            {/* Notifications Card */}
            {notificationsEnabled && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={t("notifications")}
                  icon="notifications"
                  summaryItems={apartmentsInError.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))}
                  range={areasRange}
                  onRangeChange={setAreasRange}
                  showFilter={false}
                  showEvents={false}
                  events={[]}
                  onClick={() =>
                    navigate(
                      location?.pathname?.startsWith("/client")
                        ? "/client/notifications"
                        : "/notifications"
                    )
                  }
                />
              </Grid>
            )}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

Dashboard.defaultProps = {
  apartmentsInError: [0, 0],
  onnaDevices: {},
  onnaDevicesData: [0, 0],
  projects: {},
  // currentProyectId: null,
};

Dashboard.propTypes = {
  apartmentsInError: PropTypes.array,
  onnaDevices: PropTypes.object,
  onnaDevicesData: PropTypes.array,
  sendGetOnnaDevicesState: PropTypes.func.isRequired,
  menuConfig: PropTypes.object.isRequired,
  chatSummary: PropTypes.object.isRequired,
  chatEvents: PropTypes.array.isRequired,
  projects: PropTypes.object,
  // currentProyectId: PropTypes.string,
};

const getFormatedDate = (start) => {
  if (!start) return "";

  const startMoment = moment(start);

  let dayLabel = startMoment.format("DD/MM/YY");

  if (startMoment.isSame(moment(), "day")) {
    dayLabel = "Today";
  } else if (startMoment.isSame(moment().add(1, "day"), "day")) {
    dayLabel = "Tomorrow";
  }

  return `${dayLabel} - ${startMoment.format("HH:mm")}`;
};

const mapStateToProps = (state) => {
  const {
    notificationsReducer,
    onnaDevicesReducer,
    clientsReducer,
    clientReservationsReducer,
    projectsReducer,
    apartmentsReducer,
    mainUserReducer,
    supportReducer,
    chatReducer,
    amenitiesReducer,
    servicesReducer,
  } = state;

  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};
  const projectsProjectManager = isProjectManager ? mainUserReducer?.projects || {} : {};
  const connectedDevices = Object.values(onnaDevicesReducer || {}).filter(
    ({ connected }) => connected === true
  ).length;

  const totalDevices = Object.keys(onnaDevicesReducer || {}).length;

  const { notificationsByApartment = {}, badgeNumber, totalNumber = 0 } = notificationsReducer;

  const visibleNotificationsByApartment = Object.values(notificationsByApartment).filter((ap) =>
    isProjectManager ? projectsProjectManager?.[ap.projectId] : true
  );

  const unreadAppartments = visibleNotificationsByApartment.filter(
    (ap) => ap.unreadNumber > 0
  ).length;

  const projectKeys = Object.keys(projectsReducer);
  const currentProyectId = projectKeys.length === 1 ? projectsReducer[projectKeys[0]].uid : "home";

  const isAuthorizedProjectId = (projectId) => !isProjectManager || !!userProjects?.[projectId];

  // dates

  const now = moment();
  const last24HoursStart = now.clone().subtract(24, "hours");

  const todayStart = now.clone().startOf("day");
  const todayEnd = now.clone().endOf("day");
  const start30 = now.clone().startOf("day");
  const end30 = now.clone().add(30, "days").endOf("day");

  // amenities
  const clientReservationsListAll = Object.values(
    clientReservationsReducer?.clientReservations || {}
  ).flatMap((reservationsByClient) =>
    reservationsByClient && typeof reservationsByClient === "object"
      ? Object.values(reservationsByClient)
      : []
  );

  const clientReservationsList = clientReservationsListAll.filter((reservation) =>
    isAuthorizedProjectId(reservation?.projectId)
  );

  const clientReservationById = clientReservationsList.reduce((acc, reservation) => {
    if (reservation?.uid) {
      acc[reservation.uid] = reservation;
    }
    return acc;
  }, {});

  const getAuthorizedProjectIdFromEvent = (event = {}, clientReservationId = "") => {
    const projectIdFromEvent = event?.projectId;
    if (projectIdFromEvent) return projectIdFromEvent;

    const projectIdFromReservation = clientReservationById?.[clientReservationId]?.projectId;
    if (projectIdFromReservation) return projectIdFromReservation;

    const apartmentId = event?.apartmentId || event?.extendedProps?.apartmentId;
    const projectIdFromApartment = apartmentsReducer?.[apartmentId]?.projectId;
    if (projectIdFromApartment) return projectIdFromApartment;

    return "";
  };

  const amenityEventsRaw = Object.values(amenitiesReducer?.amenityEvents || {})
    .flatMap((eventsByClientReservation) =>
      Object.entries(eventsByClientReservation || {}).flatMap(([clientReservationId, eventsById]) =>
        Object.values(eventsById || {}).map((event) => ({
          ...event,
          clientReservationId: event?.clientReservationId || clientReservationId,
        }))
      )
    )
    .filter((ev) => ev && typeof ev === "object");

  const amenityEventsFiltered = amenityEventsRaw.filter((ev) => {
    const eventProjectId = getAuthorizedProjectIdFromEvent(ev, ev?.clientReservationId);
    return isAuthorizedProjectId(eventProjectId);
  });

  const amenities = amenitiesReducer?.amenities || {};

  const amenityEvents = amenityEventsFiltered
    .filter((ev) => moment(ev.start).isBetween(start30, end30, null, "[]"))
    .sort((a, b) => moment(a.start).diff(moment(b.start)))
    .map((ev) => ({
      id: ev.id,
      title: amenities[ev.amenityId]?.name,
      subtitle: ev.title,
      time: getFormatedDate(ev.start),
    }));

  const amenityEventsToday = amenityEventsFiltered.filter((ev) =>
    moment(ev.start).isBetween(todayStart, todayEnd, null, "[]")
  ).length;

  const upcomingAmenityEvents = amenityEventsFiltered.filter((ev) =>
    moment(ev.start).isAfter(todayEnd)
  ).length;

  const amenityEventsSummary = {
    today: amenityEventsToday,
    upcoming: upcomingAmenityEvents,
  };

  // services
  const serviceEventsRaw = Object.values(servicesReducer?.serviceEvents || {})
    .flatMap((eventsByClientReservation) =>
      Object.entries(eventsByClientReservation || {}).flatMap(([clientReservationId, eventsById]) =>
        Object.values(eventsById || {}).map((event) => ({
          ...event,
          clientReservationId: event?.clientReservationId || clientReservationId,
        }))
      )
    )
    .filter((ev) => ev && typeof ev === "object");

  const serviceEventsFiltered = serviceEventsRaw.filter((ev) => {
    const eventProjectId = getAuthorizedProjectIdFromEvent(ev, ev?.clientReservationId);
    return isAuthorizedProjectId(eventProjectId);
  });

  const services = servicesReducer.services || {};

  const serviceEvents = serviceEventsFiltered
    .filter((ev) => moment(ev.start).isBetween(start30, end30, null, "[]"))
    .sort((a, b) => moment(a.start).diff(moment(b.start)))
    .map((ev) => ({
      id: ev.id,
      title: services[ev.serviceId]?.name || ev.title || "-",
      subtitle: ev.title,
      time: getFormatedDate(ev.start),
    }));

  const serviceEventsToday = serviceEventsFiltered.filter((ev) =>
    moment(ev.start).isBetween(todayStart, todayEnd, null, "[]")
  ).length;

  const upcomingServiceEvents = serviceEventsFiltered.filter((ev) =>
    moment(ev.start).isAfter(todayEnd)
  ).length;

  const serviceEventsSummary = {
    today: serviceEventsToday,
    upcoming: upcomingServiceEvents,
  };

  const activeClientReservationIds =
    getActiveClientReservationIdsForSupportCases(clientReservationsList);

  // soporte
  const supportCases = Object.values(supportReducer || {})
    .filter((supportCase) => supportCase && typeof supportCase === "object")
    .filter((supportCase) => isAuthorizedProjectId(supportCase?.projectId));

  const openSupportCases = supportCases.filter(
    (sc) => sc.status === "pending" && isSupportActive(sc, activeClientReservationIds)
  );
  const inProgressSupportCases = supportCases.filter(
    (sc) => sc.status === "in progress" && isSupportActive(sc, activeClientReservationIds)
  ).length;
  const solvedSupportCases = supportCases.filter((sc) => sc.status === "solved").length;

  const supportSummary = {
    open: openSupportCases.length,
    inProgress: inProgressSupportCases,
    solved: solvedSupportCases,
  };

  const supportEvents = openSupportCases
    .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
    .map((sc) => ({
      id: sc.supportCaseId,
      title: sc.title,
      subtitle: projectsReducer[sc.projectId]?.name || "",
      time: getFormatedDate(sc.createdAt),
    }));

  // chat
  const messagesByClientReservation = chatReducer?.messagesByClientReservation || {};
  const apartmentsMetaByClientReservation = chatReducer?.apartmentsMetaByClientReservation || {};

  const chatThreadIds = new Set([
    ...Object.keys(apartmentsMetaByClientReservation || {}),
    ...Object.keys(messagesByClientReservation || {}),
  ]);

  const chatThreads = [...chatThreadIds]
    .map((clientReservationId) => {
      const reservation = clientReservationById[clientReservationId] || {};
      const projectId = reservation?.projectId;
      const apartmentId = reservation?.apartmentId;
      const clientId = reservation?.clientId || reservation?.clientUserId;
      const client = clientsReducer?.[clientId] || {};
      const startDate = Number(reservation?.startDate);
      const endDate = Number(reservation?.endDate);

      const meta = apartmentsMetaByClientReservation[clientReservationId] || {};
      const messages = Object.values(messagesByClientReservation[clientReservationId] || {});

      const lastMessageFromMessages = messages
        .filter((m) => m && typeof m === "object")
        .sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0))[0];

      const lastMessage = meta?.lastMessage || lastMessageFromMessages || null;
      const lastMessageAt = Number(lastMessage?.createdAt || 0);
      const lastReadMaster = Number(meta?.lastRead?.master || 0);
      const isLastMessageDeleted = Boolean(lastMessage?.deleted);

      return {
        clientReservationId,
        projectId,
        apartmentId,
        projectName: projectsReducer?.[projectId]?.name || "",
        clientName:
          `${client?.firstName || ""} ${client?.lastName || ""}`.trim() ||
          client?.email ||
          clientId ||
          "-",
        apartmentName: apartmentsReducer?.[apartmentId]?.name || "",
        reservationRange: `${
          Number.isFinite(startDate) ? moment(startDate).format("DD/MM/YYYY") : "-"
        } - ${Number.isFinite(endDate) ? moment(endDate).format("DD/MM/YYYY") : "-"}`,
        isActive: activeClientReservationIds.has(clientReservationId),
        unread: !isLastMessageDeleted && lastMessageAt > 0 && lastMessageAt > lastReadMaster,
        lastMessage,
        lastMessageAt,
      };
    })
    .filter((thread) => thread?.lastMessageAt > 0)
    .filter((thread) => isAuthorizedProjectId(thread?.projectId));

  const chatSummary = {
    unread: chatThreads.filter((thread) => thread?.unread).length,
    active: chatThreads.filter((thread) => thread?.isActive).length,
    // total: chatThreads.length,
  };

  const chatEvents = chatThreads
    .filter((thread) => thread?.unread)
    .sort((a, b) => Number(b?.lastMessageAt || 0) - Number(a?.lastMessageAt || 0))
    .map((thread) => ({
      id: thread.clientReservationId,
      title: `${thread.projectName || "-"} - ${thread.clientName || "-"} - ${
        thread.apartmentName || "-"
      } (${thread.reservationRange})`,
      subtitle: thread?.lastMessage?.text || "",
      time: getFormatedDate(thread.lastMessageAt),
    }));

  const totalUnreadNotifications = visibleNotificationsByApartment.reduce(
    (sum, apartmentData) => sum + Number(apartmentData?.unreadNumber || 0),
    0
  );

  const notificationsLast24h = visibleNotificationsByApartment.reduce((sum, apartmentData) => {
    const apartmentNotifications = Object.values(apartmentData?.notifications || {});
    const recentNotifications = apartmentNotifications.filter((notification) =>
      moment(Number(notification?.createdAt || 0)).isBetween(last24HoursStart, now, null, "[]")
    ).length;

    return sum + recentNotifications;
  }, 0);

  const notifications = [
    { label: "notRead", value: totalUnreadNotifications || badgeNumber },
    { label: "last24h", value: notificationsLast24h },
    { label: "read", value: Math.max(0, totalNumber - (totalUnreadNotifications || badgeNumber)) },
  ];

  const apartmentsInError = [
    {
      label: "apartmentsWithUnread",
      value: `${unreadAppartments}`,
    },
    { label: "unreadNotifications", value: totalUnreadNotifications || badgeNumber },
    { label: "notificationsLast24h", value: notificationsLast24h },
  ];

  const onnaDevicesData = [
    { label: "connected", value: connectedDevices },
    { label: "disconnected", value: totalDevices - connectedDevices },
  ];

  return {
    notifications,
    apartmentsInError,
    onnaDevicesData,
    onnaDevices: onnaDevicesReducer,
    users: Object.keys(clientsReducer).length,
    clientReservations: clientReservationsList.length,
    projects: projectsReducer,
    menuConfig: mainUserReducer.customApp.menuConfig,
    mainUserId: mainUserReducer?.uid || null,
    currentProyectId,
    supportCases,
    supportSummary,
    supportEvents,
    chatSummary,
    chatEvents,
    amenityEvents,
    amenityEventsSummary,
    serviceEvents,
    serviceEventsSummary,
    isProjectManager,
  };
};

const mapDispatchToProps = (dispatch) => ({
  sendGetOnnaDevicesState: (onnaIds) => {
    dispatch(getOnnaDevicesState(onnaIds));
  },

  actGetServices: () => dispatch(getAllServices()),
  actGetAllServiceEvents: () => {
    dispatch(getServiceEvents());
  },

  actGetAmenities: () => dispatch(getAllAmenities()),
  actGetAllAmenityEvents: () => {
    dispatch(getAmenityEvents());
  },

  actGetAllSupportCases: () => dispatch(getAllSupportCases()),
  actGetAllChatMessages: () => dispatch(getAllChatMessages()),

  actGetClients: () => dispatch(updateClients()),
  actGetClientReservations: () => dispatch(updateClientReservations()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  memo(Dashboard, (prevProps, nextProps) => {
    if (!(JSON.stringify(prevProps.notifications) === JSON.stringify(nextProps.notifications)))
      return false;
    if (!(JSON.stringify(prevProps.onnaDevicesData) === JSON.stringify(nextProps.onnaDevicesData)))
      return false;
    if (!(JSON.stringify(prevProps.onnaDevices) === JSON.stringify(nextProps.onnaDevices)))
      return false;
    if (!(JSON.stringify(prevProps.users) === JSON.stringify(nextProps.users))) return false;
    if (
      !(
        JSON.stringify(prevProps.clientReservations) ===
        JSON.stringify(nextProps.clientReservations)
      )
    )
      return false;
    if (!(JSON.stringify(prevProps.supportCases) === JSON.stringify(nextProps.supportCases)))
      return false;
    if (!(JSON.stringify(prevProps.supportSummary) === JSON.stringify(nextProps.supportSummary)))
      return false;
    if (!(JSON.stringify(prevProps.supportEvents) === JSON.stringify(nextProps.supportEvents)))
      return false;
    if (!(JSON.stringify(prevProps.chatSummary) === JSON.stringify(nextProps.chatSummary)))
      return false;
    if (!(JSON.stringify(prevProps.chatEvents) === JSON.stringify(nextProps.chatEvents)))
      return false;
    if (!(JSON.stringify(prevProps.amenityEvents) === JSON.stringify(nextProps.amenityEvents)))
      return false;
    if (
      !(
        JSON.stringify(prevProps.amenityEventsSummary) ===
        JSON.stringify(nextProps.amenityEventsSummary)
      )
    )
      return false;
    if (!(JSON.stringify(prevProps.serviceEvents) === JSON.stringify(nextProps.serviceEvents)))
      return false;

    if (
      !(
        JSON.stringify(prevProps.serviceEventsSummary) ===
        JSON.stringify(nextProps.serviceEventsSummary)
      )
    )
      return false;

    return true;
  })
);
